<template>
  <div class="page-shell">
    <header class="topbar">
      <div class="brand">
        <img class="brand-mark" src="/logo.png" alt="太一排盘" />
        <div class="brand-copy">
          <div class="brand-name">太一黄历</div>
          <div class="brand-note">节气时令 · 干支历法 · 日用黄历</div>
        </div>
      </div>
    </header>

    <main>
      <nav class="view-nav" aria-label="页面切换">
        <button
          v-for="view in viewOptions"
          :key="view.key"
          type="button"
          :class="['view-nav-btn', { 'view-nav-btn--active': activeView === view.key }]"
          @click="switchView(view.key)"
        >
          {{ view.label }}
        </button>
        <a
          v-for="link in stationLinks"
          :key="link.key"
          class="view-nav-link"
          :href="link.href"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ link.label }}
        </a>
      </nav>

      <template v-if="activeView === 'home'">
      <section class="hero">
        <div class="hero-copy">

        </div>

        <section class="hero-calendar-shell">
          <div class="hero-calendar-top">
            <div class="hero-calendar-copy">
              <span class="summary-label">太一万年历·老黄历·通胜</span>

            </div>

            <div class="hero-calendar-tools">
              <div class="calendar-select-wrap">
                <select v-model.number="calendarYear" class="calendar-select" aria-label="选择年份">
                  <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}年</option>
                </select>
              </div>
              <button class="month-btn month-btn--icon" @click="goPrevMonth" aria-label="上月">‹</button>
              <div class="calendar-select-wrap calendar-select-wrap--month">
                <select v-model.number="calendarMonth" class="calendar-select" aria-label="选择月份">
                  <option v-for="month in monthOptions" :key="month" :value="month">{{ month }}月</option>
                </select>
              </div>
              <button class="month-btn month-btn--icon" @click="goNextMonth" aria-label="下月">›</button>
              <button class="month-btn month-btn--today" @click="resetToday">今天</button>
            </div>
          </div>

          <div class="weekday-row weekday-row--hero">
            <span v-for="name in weekNames" :key="`hero-${name}`">{{ name }}</span>
          </div>

          <div class="calendar-grid calendar-grid--hero">
            <template v-for="(week, weekIndex) in calendarView.weeks" :key="`hero-week-${weekIndex}`">
              <button
                v-for="cell in week"
                :key="`hero-${cell.key}`"
                type="button"
                :class="getCalendarCellClass(cell)"
                @click="selectDay(cell.key)"
              >
                <span v-if="cell.isToday" class="calendar-today-badge">今</span>
                <span class="cell-solar">{{ cell.solarDay }}</span>
                <span class="cell-lunar">{{ cell.lunarText }}</span>
              </button>
            </template>
          </div>

          <div class="hero-day-strip">
            <div class="hero-day-primary">
              <div class="hero-day-date">{{ selectedDetail.lunarText }}</div>
              <div class="hero-day-meta">{{ selectedDetail.yearPillar }}年 {{ selectedDetail.zodiac }} · {{ selectedDetail.solarLabel }}</div>
            </div>

            <div class="hero-day-actions">
              <div class="hero-day-row hero-day-row--good">
                <span class="hero-day-tag">宜</span>
                <span class="hero-day-text">{{ selectedDetail.recommends.slice(0, 6).join(' · ') }}</span>
              </div>
              <div class="hero-day-row hero-day-row--bad">
                <span class="hero-day-tag">忌</span>
                <span class="hero-day-text">{{ selectedDetail.avoids.slice(0, 6).join(' · ') }}</span>
              </div>
            </div>

            <div class="hero-day-footer">
              <span class="hero-day-selected">{{ selectedDetail.solarLabel }}</span>
              <span v-if="upcomingLead" class="hero-day-upcoming">距离{{ upcomingLead.name }}还有{{ upcomingLead.daysAway }}天</span>
            </div>
          </div>
        </section>
      </section>

      <section class="content-grid">
        <article class="almanac-card">
          <div class="panel-head">
            <div>
              <span class="panel-kicker">日用黄历</span>
              <h2>{{ selectedDetail.solarLabel }}</h2>
              <p class="panel-subtitle">{{ selectedDetail.lunarLabel }}</p>
            </div>
            <div class="festival-chips" v-if="selectedDetail.festivals.length">
              <span
                v-for="festival in selectedDetail.festivals"
                :key="festival.name"
                :class="['festival-chip', `festival-chip--${festival.tone}`]"
              >
                {{ festival.name }}
              </span>
            </div>
          </div>

          <div class="pillar-grid">
            <div class="pillar-item">
              <span>年柱</span>
              <strong>{{ selectedDetail.yearPillar }}</strong>
            </div>
            <div class="pillar-item">
              <span>月柱</span>
              <strong>{{ selectedDetail.monthPillar }}</strong>
            </div>
            <div class="pillar-item">
              <span>日柱</span>
              <strong>{{ selectedDetail.dayPillar }}</strong>
            </div>
            <div class="pillar-item">
              <span>时柱</span>
              <strong>{{ selectedDetail.hourPillar }}</strong>
            </div>
          </div>

          <section class="detail-table-card">
            <div class="table-title">当日详览</div>
            <div class="detail-table">
              <div v-for="row in selectedDetail.summaryRows" :key="row.label" class="detail-row">
                <div class="detail-label">{{ row.label }}</div>
                <div class="detail-value">{{ row.value }}</div>
              </div>
            </div>
          </section>

          <section class="hour-panel">
            <div class="table-title">时辰吉凶</div>
            <div class="hour-grid">
              <div v-for="hour in selectedDetail.hourLuck" :key="hour.name" class="hour-item">
                <div class="hour-head">
                  <strong>{{ hour.label }}</strong>
                  <span :class="['hour-luck', hour.luck === '吉' ? 'hour-luck--good' : 'hour-luck--bad']">{{ hour.luck }}</span>
                </div>
                <div class="hour-meta">{{ hour.keeper }} · {{ hour.channel }}</div>
                <div class="hour-reco" v-if="hour.recommends.length">{{ hour.recommends.join(' / ') }}</div>
              </div>
            </div>
          </section>
        </article>

        <aside class="sidebar">
          <section class="panel info-panel">
            <div class="panel-kicker">当前节令</div>
            <h3>{{ todaySnapshot.termName }}</h3>
            <p>{{ todaySnapshot.termLabel }}</p>
            <div class="info-detail">交节时刻：{{ todaySnapshot.termTime }}</div>
            <div class="info-detail">下一个节气：{{ todaySnapshot.nextTermName }} · {{ todaySnapshot.nextTermTime }}</div>
            <div class="info-foot">{{ todaySnapshot.daysToNextTerm }}天后进入{{ todaySnapshot.nextTermName }}</div>
          </section>

          <section class="panel event-panel">
            <div class="panel-kicker">近期节令与节日</div>
            <ul class="event-list">
              <li v-for="event in upcomingEvents" :key="`${event.dateLabel}-${event.name}`">
                <span class="event-name">{{ event.name }}</span>
                <span :class="['event-tone', `event-tone--${event.tone}`]">{{ event.dateLabel }}</span>
                <span class="event-days">{{ event.displayDays }}</span>
              </li>
            </ul>
          </section>

          <section class="panel pengzu-panel">
            <div class="panel-kicker">彭祖百忌</div>
            <h3>{{ selectedDetail.pengZu }}</h3>

          </section>

          <section class="panel tibetan-panel">
            <div class="panel-kicker">藏历</div>
            <h3>{{ selectedDetail.rabByungLabel }}</h3>

          </section>
        </aside>
      </section>

      </template>

      <template v-else-if="activeView === 'solar-terms'">
        <section class="terms-page">
          <div class="terms-hero">
            <div class="terms-hero-copy">
              <span class="hero-kicker">节气时令</span>
              <h1>顺四时之序，观二十四节气</h1>
            </div>

            <div class="terms-hero-tools">
              <div class="calendar-select-wrap">
                <select v-model.number="solarTermYear" class="calendar-select" aria-label="选择节气年份">
                  <option v-for="year in yearOptions" :key="`term-${year}`" :value="year">{{ year }}年</option>
                </select>
              </div>
            </div>
          </div>

          <section v-if="selectedTerm" class="terms-highlight">
            <div class="terms-highlight-head">
              <div>
                <span class="panel-kicker">{{ selectedTerm.isCurrent ? '当前节令' : '已选节令' }}</span>
                <h2>{{ selectedTerm.name }}</h2>
                <p class="terms-highlight-sub">{{ selectedTerm.dateLabel }} · {{ selectedTerm.timeLabel }} · {{ selectedTerm.kind }}</p>
              </div>
              <div class="terms-meta-chips">
                <span :class="['terms-chip', { 'terms-chip--current': selectedTerm.isCurrent }]">
                  {{ selectedTerm.isCurrent ? '此刻所在节令' : '年度节令节点' }}
                </span>
                <span class="terms-chip">{{ selectedTerm.termDayLabel }}</span>
                <span class="terms-chip">{{ selectedTerm.phaseLabel }}</span>
                <span class="terms-chip">{{ selectedTerm.constellation }}座</span>
              </div>
            </div>

            <div class="terms-detail-grid">
              <article class="terms-detail-card">
                <div class="table-title">交节信息</div>
                <div class="terms-detail-list">
                  <div class="terms-detail-item">
                    <span>交节日期</span>
                    <strong>{{ selectedTerm.solarLabel }}</strong>
                  </div>
                  <div class="terms-detail-item">
                    <span>交节时刻</span>
                    <strong>{{ selectedTerm.timeLabel }}</strong>
                  </div>
                  <div class="terms-detail-item">
                    <span>当前物候</span>
                    <strong>{{ selectedTerm.phenologyLabel }}</strong>
                  </div>
                  <div class="terms-detail-item">
                    <span>藏历对应</span>
                    <strong>{{ selectedTerm.rabByungLabel }}</strong>
                  </div>
                </div>
              </article>

              <article class="terms-detail-card">
                <div class="table-title">三候物象</div>
                <div class="terms-phenology-grid">
                  <div v-for="item in selectedTerm.phenologies" :key="`${selectedTerm.key}-${item.stage}`" class="terms-phenology-item">
                    <span>{{ item.stage }}</span>
                    <strong>{{ item.name }}</strong>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section class="terms-grid-section">
            <div class="section-header section-header--tight">
              <span class="section-kicker">全年节令</span>
              <h2>{{ solarTermsView.title }}</h2>
              <p>按年份完整浏览二十四节气，并可点选任一节令查看交节信息与三候物象。</p>
            </div>

            <div class="terms-grid">
              <button
                v-for="term in solarTermsView.terms"
                :key="term.key"
                type="button"
                :class="['terms-card', { 'terms-card--active': selectedTerm && selectedTerm.key === term.key, 'terms-card--current': term.isCurrent }]"
                @click="selectTerm(term.key)"
              >
                <div class="terms-card-top">
                  <span class="terms-card-name">{{ term.name }}</span>
                  <span class="terms-card-kind">{{ term.kind }}</span>
                </div>
                <div class="terms-card-date">{{ term.dateLabel }}</div>
                <div class="terms-card-time">{{ term.timeLabel }}</div>
                <div class="terms-card-note">{{ term.phenologyLabel }}</div>
              </button>
            </div>
          </section>

        </section>
      </template>
    </main>

    <footer class="site-footer">
      <div class="footer-title">太一黄历</div>
      <div class="footer-meta">
        <div>Email: <a :href="`mailto:${site.supportEmail}`">{{ site.supportEmail }}</a></div>
        <div v-if="site.isCn" class="records">
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">{{ site.records.icp }}</a>
          <a href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=41038102000315" target="_blank" rel="noopener noreferrer">{{ site.records.public }}</a>
        </div>
        <div>&copy; 2026 {{ site.hostLabel }} · 弘扬传统文化</div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import {
  buildDayDetail,
  buildMonthCalendar,
  buildSolarTermsYear,
  buildTodaySnapshot,
  buildUpcomingEvents,
  getMonthAnchorKey,
  shiftMonthAnchorKey,
  toDateKey
} from './lib/almanac';
import { siteConfig } from './config/site';

const now = new Date();
const todayKey = toDateKey(now);

function getInitialView() {
  if (typeof window === 'undefined') {
    return 'home';
  }
  const stateView = window.history.state?.view;
  return stateView === 'solar-terms' ? 'solar-terms' : 'home';
}

const site = siteConfig;
const activeView = ref(getInitialView());
const selectedKey = ref(todayKey);
const calendarAnchorKey = ref(getMonthAnchorKey(now));
const solarTermYear = ref(now.getFullYear());
const selectedTermKey = ref('');

const weekNames = ['一', '二', '三', '四', '五', '六', '日'];
const yearOptions = Array.from({ length: 11 }, (_, index) => now.getFullYear() - 5 + index);
const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);
const viewOptions = [
  { key: 'home', label: '黄历首页' },
  { key: 'solar-terms', label: '节气时令' }
];
const stationLinks = computed(() => {
  const suffix = site.isCn ? 'cn' : 'com';
  return [
    { key: 'bazi', label: '八字排盘', href: `https://bazi.taiyipan.${suffix}/` },
    { key: 'qimen', label: '奇门遁甲', href: `https://qimen.taiyipan.${suffix}/` },
    { key: 'fengshui', label: '流年飞星', href: `https://fs.taiyipan.${suffix}/` },
    { key: 'liuyao', label: '六爻占卜', href: `https://liuyao.taiyipan.${suffix}/` }
  ];
});

const todaySnapshot = computed(() => buildTodaySnapshot(new Date()));
const upcomingEvents = computed(() => buildUpcomingEvents(new Date(), 4));
const upcomingLead = computed(() => upcomingEvents.value[0] || null);
const selectedDetail = computed(() => buildDayDetail(selectedKey.value, new Date()));
const calendarView = computed(() => buildMonthCalendar(calendarAnchorKey.value, selectedKey.value, new Date()));
const solarTermsView = computed(() => buildSolarTermsYear(solarTermYear.value, new Date()));
const selectedTerm = computed(
  () => solarTermsView.value.terms.find((term) => term.key === selectedTermKey.value) || solarTermsView.value.terms[0] || null
);
const calendarYear = computed({
  get() {
    return Number(calendarAnchorKey.value.slice(0, 4));
  },
  set(value) {
    calendarAnchorKey.value = `${String(value)}-${calendarAnchorKey.value.slice(5, 7)}-01`;
  }
});
const calendarMonth = computed({
  get() {
    return Number(calendarAnchorKey.value.slice(5, 7));
  },
  set(value) {
    calendarAnchorKey.value = `${calendarAnchorKey.value.slice(0, 4)}-${String(value).padStart(2, '0')}-01`;
  }
});

function selectDay(key) {
  selectedKey.value = key;
  calendarAnchorKey.value = `${key.slice(0, 7)}-01`;
}

function resetToday() {
  selectDay(todayKey);
}

function syncViewFromHistory() {
  activeView.value = getInitialView();
}

function switchView(viewKey) {
  activeView.value = viewKey;
  if (typeof window !== 'undefined') {
    window.history.pushState({ view: viewKey }, '', `${window.location.pathname}${window.location.search}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function goPrevMonth() {
  calendarAnchorKey.value = shiftMonthAnchorKey(calendarAnchorKey.value, -1);
}

function goNextMonth() {
  calendarAnchorKey.value = shiftMonthAnchorKey(calendarAnchorKey.value, 1);
}

function getCalendarCellClass(cell) {
  return {
    'calendar-day': true,
    'calendar-day--muted': !cell.isCurrentMonth,
    'calendar-day--today': cell.isToday,
    'calendar-day--selected': cell.isSelected,
    'calendar-day--rest': cell.markerTone === 'rest',
    'calendar-day--festival': cell.markerTone === 'festival',
    'calendar-day--term': cell.markerTone === 'term',
    'calendar-day--work': cell.markerTone === 'work'
  };
}

function selectTerm(termKey) {
  selectedTermKey.value = termKey;
}

watch(
  solarTermsView,
  (data) => {
    if (!data.terms.some((term) => term.key === selectedTermKey.value)) {
      selectedTermKey.value = data.currentTermKey || data.terms[0]?.key || '';
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.history.replaceState({ view: activeView.value }, '', `${window.location.pathname}${window.location.search}`);
    window.addEventListener('popstate', syncViewFromHistory);
  }
});
</script>

<style lang="scss">
.page-shell {
  width: min(1280px, calc(100% - 40px));
  margin: 0 auto;
  padding: 22px 8px 40px;
}

.topbar,
.brand,
.summary-chips,
.pillar-grid,
.festival-chips,
.card-grid,
.footer-meta,
.records {
  display: flex;
  flex-wrap: wrap;
}

.topbar {
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 0 28px;
}

.brand {
  align-items: center;
  gap: 14px;
}

.brand-mark {
  width: 44px;
  height: 44px;
  display: block;
  object-fit: contain;
  filter: drop-shadow(0 10px 18px rgba(122, 73, 226, 0.12));
}

.brand-name {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.brand-note,
.top-note,
.hero-desc,
.summary-sub,
.summary-detail,
.summary-foot,
.panel-subtitle,
.detail-value,
.hour-meta,
.hour-reco,
.section-header p,
.card p,
.footer-desc,
.footer-meta {
  color: var(--hl-text-secondary);
  line-height: 1.85;
}

.brand-note,
.top-note {
  max-width: 460px;
  text-align: right;
}

.view-nav {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.view-nav-btn,
.view-nav-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid rgba(122, 73, 226, 0.12);
  border-radius: var(--hl-radius-pill);
  background: rgba(255, 255, 255, 0.74);
  color: var(--hl-text-primary);
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.view-nav-btn:hover,
.view-nav-link:hover {
  transform: translateY(-1px);
}

.view-nav-btn--active {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, var(--hl-brand-primary), #8c74f7);
  box-shadow: 0 10px 20px rgba(122, 73, 226, 0.18);
}

.hero {
  padding-bottom: 10px;
}

.hero-copy {
  max-width: 780px;
}

.hero-kicker,
.panel-kicker,
.section-kicker,
.summary-label,
.card-tag {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 14px;
  border-radius: var(--hl-radius-pill);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.hero-kicker,
.panel-kicker,
.section-kicker,
.summary-label {
  color: var(--hl-brand-primary);
  background: rgba(122, 73, 226, 0.08);
}

.hero h1,
.summary-main,
.panel-title,
.panel-head h2,
.section-header h2,
.card h3,
.footer-title {
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  color: var(--hl-text-primary);
  font-weight: 600;
}

.hero h1 {
  margin: 18px 0 0;
  font-size: clamp(34px, 5vw, 58px);
  line-height: 1.12;
  letter-spacing: 0.08em;
}

.hero-desc {
  margin: 14px 0 0;
  max-width: 720px;
  font-size: 15px;
}

.hero-calendar-shell {
  margin-top: 26px;
  padding: 22px 22px 20px;
  border: 1px solid var(--hl-border-regular);
  border-radius: 30px;
  background:
    radial-gradient(circle at top right, rgba(157, 123, 240, 0.08), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(247, 242, 255, 0.74));
  box-shadow: var(--hl-shadow-card);
  backdrop-filter: blur(8px);
}

.terms-page {
  padding-top: 4px;
}

.terms-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.terms-hero-copy {
  max-width: 760px;
}

.terms-hero-tools {
  display: flex;
  align-items: center;
  gap: 10px;
}

.terms-highlight,
.terms-grid-section,
.terms-link-section {
  margin-top: 24px;
  padding: 24px 24px 22px;
  border: 1px solid var(--hl-border-regular);
  border-radius: 28px;
  background:
    radial-gradient(circle at top right, rgba(157, 123, 240, 0.08), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(247, 242, 255, 0.74));
  box-shadow: var(--hl-shadow-card);
  backdrop-filter: blur(8px);
}

.terms-highlight-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.terms-highlight h2 {
  margin: 14px 0 0;
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  color: var(--hl-text-primary);
  font-size: clamp(30px, 4vw, 42px);
  font-weight: 600;
  line-height: 1.2;
}

.terms-highlight-sub {
  margin: 10px 0 0;
  color: var(--hl-text-secondary);
  font-size: 14px;
}

.terms-meta-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.terms-chip {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: var(--hl-radius-pill);
  background: rgba(122, 73, 226, 0.08);
  color: var(--hl-text-secondary);
  font-size: 12px;
  font-weight: 700;
}

.terms-chip--current {
  background: rgba(79, 127, 114, 0.12);
  color: #3d7268;
}

.terms-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 20px;
}

.terms-detail-card {
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(122, 73, 226, 0.08);
  background: rgba(255, 255, 255, 0.56);
}

.terms-detail-list {
  display: grid;
  gap: 12px;
  margin-top: 14px;
}

.terms-detail-item {
  display: grid;
  gap: 6px;
}

.terms-detail-item span,
.terms-phenology-item span,
.terms-card-time {
  color: var(--hl-text-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.terms-detail-item strong,
.terms-phenology-item strong {
  color: var(--hl-text-primary);
  font-weight: 600;
  line-height: 1.65;
}

.terms-phenology-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.terms-phenology-item {
  padding: 14px 12px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(122, 73, 226, 0.06), rgba(255, 255, 255, 0.9));
  border: 1px solid rgba(122, 73, 226, 0.08);
}

.section-header--tight {
  max-width: none;
}

.terms-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 20px;
}

.terms-link-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 20px;
  padding: 22px;
  border-radius: 24px;
  border: 1px solid rgba(122, 73, 226, 0.1);
  background:
    radial-gradient(circle at top right, rgba(122, 73, 226, 0.08), transparent 32%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.78), rgba(245, 239, 255, 0.92));
}

.terms-link-copy {
  max-width: 760px;
}

.terms-link-copy h3 {
  margin: 14px 0 0;
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  color: var(--hl-text-primary);
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
}

.terms-link-copy p {
  margin: 12px 0 0;
  color: var(--hl-text-secondary);
  font-size: 14px;
  line-height: 1.85;
}

.terms-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 168px;
  min-height: 48px;
  padding: 0 24px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(122, 73, 226, 0.94), rgba(154, 111, 236, 0.94));
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 14px 30px rgba(122, 73, 226, 0.18);
}

.terms-card {
  padding: 16px 16px 14px;
  border: 1px solid rgba(122, 73, 226, 0.08);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.64);
  text-align: left;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.terms-card:hover {
  transform: translateY(-1px);
}

.terms-card--active {
  border-color: rgba(122, 73, 226, 0.28);
  background: linear-gradient(180deg, rgba(122, 73, 226, 0.1), rgba(255, 255, 255, 0.9));
  box-shadow: 0 12px 24px rgba(122, 73, 226, 0.08);
}

.terms-card--current .terms-card-kind {
  background: rgba(79, 127, 114, 0.12);
  color: #3d7268;
}

.terms-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.terms-card-name {
  color: var(--hl-text-primary);
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  font-size: 22px;
  font-weight: 600;
}

.terms-card-kind {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: var(--hl-radius-pill);
  background: rgba(198, 144, 42, 0.12);
  color: #8c6725;
  font-size: 11px;
  font-weight: 700;
}

.terms-card-date {
  margin-top: 12px;
  color: var(--hl-text-primary);
  font-size: 14px;
  font-weight: 600;
}

.terms-card-note {
  margin-top: 8px;
  color: var(--hl-text-secondary);
  font-size: 13px;
  line-height: 1.7;
}

.hero-calendar-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.hero-calendar-copy {
  max-width: 560px;
}

.hero-calendar-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.calendar-select-wrap {
  position: relative;
}

.calendar-select-wrap::after {
  content: "";
  position: absolute;
  top: 50%;
  right: 14px;
  width: 8px;
  height: 8px;
  border-right: 1.5px solid #6a6297;
  border-bottom: 1.5px solid #6a6297;
  transform: translateY(-65%) rotate(45deg);
  pointer-events: none;
}

.calendar-select {
  min-width: 110px;
  min-height: 44px;
  padding: 0 38px 0 16px;
  border-radius: 14px;
  border: 1px solid rgba(122, 73, 226, 0.14);
  background: rgba(255, 255, 255, 0.88);
  color: var(--hl-text-primary);
  font: inherit;
  font-size: 14px;
  appearance: none;
}

.calendar-select-wrap--month .calendar-select {
  min-width: 96px;
}

.weekday-row--hero {
  margin-top: 18px;
}

.calendar-grid--hero {
  margin-top: 12px;
}

.calendar-grid--hero .calendar-day {
  min-height: 82px;
}

.hero-day-strip {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 24px;
  border: 1px solid rgba(122, 73, 226, 0.08);
  background: linear-gradient(180deg, rgba(122, 73, 226, 0.04), rgba(255, 255, 255, 0.9));
}

.hero-day-date {
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  color: var(--hl-text-primary);
  font-size: 34px;
  font-weight: 600;
  line-height: 1.15;
}

.hero-day-meta {
  margin-top: 8px;
  color: var(--hl-text-secondary);
  font-size: 14px;
}

.hero-day-actions {
  display: grid;
  gap: 10px;
  margin-top: 14px;
}

.hero-day-row {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: start;
  padding: 12px 14px;
  border-radius: 18px;
}

.hero-day-row--good {
  background: rgba(79, 127, 114, 0.08);
}

.hero-day-row--bad {
  background: rgba(198, 144, 42, 0.1);
}

.hero-day-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  min-height: 28px;
  padding: 0 10px;
  border-radius: var(--hl-radius-pill);
  font-size: 12px;
  font-weight: 700;
}

.hero-day-row--good .hero-day-tag {
  background: rgba(79, 127, 114, 0.16);
  color: #3d7268;
}

.hero-day-row--bad .hero-day-tag {
  background: rgba(198, 144, 42, 0.18);
  color: #8c6725;
}

.hero-day-text {
  color: var(--hl-text-primary);
  font-size: 14px;
  line-height: 1.8;
}

.hero-day-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px 18px;
  margin-top: 14px;
  color: #5d5489;
  font-size: 13px;
}

.summary-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(320px, 0.88fr);
  align-items: start;
  gap: 22px;
  margin-top: 26px;
}

.summary-card,
.almanac-card,
.panel,
.card {
  border: 1px solid var(--hl-border-regular);
  background:
    radial-gradient(circle at top right, rgba(157, 123, 240, 0.1), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(247, 242, 255, 0.72));
  box-shadow: var(--hl-shadow-card);
  backdrop-filter: blur(8px);
}

.summary-card {
  padding: 20px 20px 18px;
  border-radius: var(--hl-radius-lg);
}

.summary-card--today {
  min-height: 0;
}

.summary-card--calendar {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.summary-card--calendar .summary-main {
  margin-top: 12px;
}

.calendar-head--summary {
  margin-top: 14px;
}

.calendar-head--summary .calendar-note {
  margin-top: 0;
  text-align: center;
}

.weekday-row--summary {
  margin-top: 14px;
}

.calendar-grid--summary {
  margin-top: 8px;
}

.summary-card--calendar .calendar-day {
  min-height: 58px;
  padding: 6px 6px;
  border-radius: 16px;
}

.summary-card--calendar .cell-solar {
  font-size: 13px;
}

.summary-card--calendar .cell-lunar {
  font-size: 9px;
  line-height: 1.35;
}

.summary-date {
  margin-top: 14px;
  color: #5d5489;
  font-size: 15px;
}

.summary-main {
  margin-top: 12px;
  font-size: 32px;
  line-height: 1.2;
}

.summary-main--medium {
  font-size: 28px;
}

.summary-sub {
  margin-top: 10px;
  font-size: 13px;
}

.summary-detail {
  margin-top: 10px;
  font-size: 13px;
}

.summary-foot {
  margin-top: 14px;
  color: #5d5489;
  font-size: 13px;
}

.summary-chips {
  gap: 8px;
  margin-top: 14px;
}

.summary-chips span {
  padding: 7px 10px;
  border-radius: var(--hl-radius-pill);
  background: rgba(122, 73, 226, 0.06);
  color: var(--hl-text-secondary);
  font-size: 12px;
}

.summary-goodbad {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.summary-goodbad-card {
  padding: 12px;
  border-radius: 18px;
  border: 1px solid rgba(122, 73, 226, 0.08);
}

.summary-goodbad-card--good {
  background: linear-gradient(180deg, rgba(79, 127, 114, 0.08), rgba(255, 255, 255, 0.84));
  border-color: rgba(79, 127, 114, 0.14);
}

.summary-goodbad-card--bad {
  background: linear-gradient(180deg, rgba(198, 144, 42, 0.09), rgba(255, 255, 255, 0.84));
  border-color: rgba(198, 144, 42, 0.14);
}

.summary-goodbad-title {
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  color: var(--hl-text-primary);
  font-size: 18px;
  font-weight: 600;
}

.summary-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.summary-tag {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: var(--hl-radius-pill);
  font-size: 12px;
}

.summary-tag--good {
  background: rgba(79, 127, 114, 0.1);
  color: #3d7268;
}

.summary-tag--bad {
  background: rgba(198, 144, 42, 0.12);
  color: #8c6725;
}

.event-list {
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

.event-list li {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 10px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px dashed rgba(122, 73, 226, 0.12);
}

.event-list li:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.event-name {
  color: var(--hl-text-primary);
  font-size: 14px;
  font-weight: 600;
}

.event-tone {
  padding: 5px 10px;
  border-radius: var(--hl-radius-pill);
  font-size: 11px;
  font-weight: 700;
}

.event-tone--rest {
  background: rgba(79, 127, 114, 0.12);
  color: #3d7268;
}

.event-tone--festival {
  background: rgba(198, 144, 42, 0.14);
  color: #8c6725;
}

.event-tone--term {
  background: rgba(122, 73, 226, 0.1);
  color: var(--hl-brand-primary);
}

.event-days {
  color: var(--hl-text-muted);
  font-size: 12px;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.56fr) minmax(320px, 0.74fr);
  align-items: flex-start;
  gap: 28px;
  padding-top: 24px;
}

.almanac-card {
  min-width: 0;
  padding: 28px 28px 26px;
  border-radius: 34px;
}

.panel-head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 18px;
}

.panel-head h2 {
  margin: 14px 0 0;
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.2;
}

.panel-subtitle {
  margin: 10px 0 0;
  font-size: 15px;
}

.festival-chips {
  gap: 10px;
  align-content: flex-start;
}

.festival-chip {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 12px;
  border-radius: var(--hl-radius-pill);
  font-size: 12px;
  font-weight: 700;
}

.festival-chip--rest {
  background: rgba(79, 127, 114, 0.12);
  color: #3d7268;
}

.festival-chip--festival {
  background: rgba(198, 144, 42, 0.14);
  color: #8c6725;
}

.festival-chip--term {
  background: rgba(122, 73, 226, 0.1);
  color: var(--hl-brand-primary);
}

.pillar-grid {
  gap: 10px;
  margin-top: 20px;
}

.pillar-item {
  flex: 1 1 138px;
  min-width: 118px;
  padding: 12px 12px 10px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.52);
  border: 1px solid rgba(122, 73, 226, 0.08);
}

.pillar-item span,
.table-title {
  color: var(--hl-text-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.pillar-item strong {
  display: block;
  margin-top: 8px;
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  font-size: 24px;
  font-weight: 600;
}

.detail-table-card,
.hour-panel {
  margin-top: 24px;
}

.detail-table {
  display: grid;
  gap: 0;
  margin-top: 14px;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(122, 73, 226, 0.08);
}

.detail-row {
  display: grid;
  grid-template-columns: 132px 1fr;
  gap: 0;
}

.detail-row:nth-child(odd) {
  background: rgba(255, 255, 255, 0.62);
}

.detail-row:nth-child(even) {
  background: rgba(122, 73, 226, 0.03);
}

.detail-label,
.detail-value {
  padding: 14px 16px;
  font-size: 14px;
}

.detail-label {
  color: #5d5489;
  font-weight: 700;
  border-right: 1px solid rgba(122, 73, 226, 0.08);
}

.hour-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.hour-item {
  padding: 12px 12px 10px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid rgba(122, 73, 226, 0.08);
}

.hour-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.hour-head strong {
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  font-size: 19px;
  font-weight: 600;
  line-height: 1.15;
}

.hour-luck {
  padding: 3px 8px;
  border-radius: var(--hl-radius-pill);
  font-size: 10px;
  font-weight: 700;
}

.hour-luck--good {
  background: rgba(79, 127, 114, 0.12);
  color: #3d7268;
}

.hour-luck--bad {
  background: rgba(198, 144, 42, 0.14);
  color: #8c6725;
}

.hour-meta {
  margin-top: 6px;
  font-size: 12px;
}

.hour-reco {
  margin-top: 6px;
  color: #5d5489;
  font-size: 11px;
  line-height: 1.6;
}

.sidebar {
  display: grid;
  gap: 16px;
  align-content: start;
}

.panel {
  min-height: 0;
  padding: 20px 20px 18px;
  border-radius: 24px;
}

.info-panel h3,
.pengzu-panel h3,
.tibetan-panel h3,
.section-header h2,
.card h3 {
  margin: 14px 0 0;
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  font-weight: 600;
}

.info-panel h3,
.pengzu-panel h3,
.tibetan-panel h3 {
  font-size: 26px;
  line-height: 1.45;
}

.info-panel p,
.pengzu-panel p,
.tibetan-panel p {
  margin: 10px 0 0;
  font-size: 13px;
}

.pengzu-panel h3,
.tibetan-panel h3 {
  font-size: 23px;
}

.pengzu-panel p,
.tibetan-panel p {
  font-size: 12px;
  line-height: 1.78;
}

.calendar-note {
  color: #5d5489;
  font-size: 12px;
}

.month-btn,
.calendar-day {
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.month-btn {
  min-height: 44px;
  border-radius: var(--hl-radius-pill);
  font: inherit;
  color: var(--hl-text-primary);
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(122, 73, 226, 0.12);
}

.info-detail,
.info-foot {
  margin-top: 10px;
  color: var(--hl-text-secondary);
  font-size: 13px;
  line-height: 1.85;
}

.info-foot {
  color: #5d5489;
  font-size: 12px;
}

.pengzu-panel {
  background:
    radial-gradient(circle at top right, rgba(198, 144, 42, 0.1), transparent 28%),
    linear-gradient(180deg, rgba(255, 251, 243, 0.96), rgba(255, 255, 255, 0.78));
}

.tibetan-panel {
  background:
    radial-gradient(circle at top right, rgba(122, 73, 226, 0.1), transparent 28%),
    linear-gradient(180deg, rgba(246, 242, 255, 0.96), rgba(255, 255, 255, 0.8));
}

.event-panel .event-list {
  margin-top: 14px;
}

.event-panel .event-list li {
  padding: 10px 0;
}

.event-panel .event-list li:last-child {
  padding-bottom: 0;
}

.calendar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.calendar-head strong {
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  font-size: 22px;
  font-weight: 600;
}

.month-btn {
  padding: 0 16px;
  font-size: 13px;
}

.month-btn--icon {
  width: 36px;
  min-width: 36px;
  padding: 0;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
}

.month-btn--today {
  min-width: 74px;
}

.weekday-row,
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.weekday-row {
  gap: 8px;
  margin-top: 18px;
}

.weekday-row span {
  text-align: center;
  color: var(--hl-text-muted);
  font-size: 12px;
  font-weight: 700;
}

.calendar-grid {
  gap: 8px;
  margin-top: 10px;
}

.calendar-day {
  position: relative;
  min-height: 88px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
  padding: 12px 10px;
  border-radius: 18px;
  border: 1px solid rgba(122, 73, 226, 0.08);
  background: rgba(255, 255, 255, 0.56);
  text-align: left;
}

.calendar-today-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--hl-brand-primary), #8c74f7);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 6px 14px rgba(122, 73, 226, 0.16);
}

.calendar-day:hover,
.month-btn:hover {
  transform: translateY(-1px);
}

.calendar-day--muted {
  opacity: 0.45;
}

.calendar-day--today {
  border-color: rgba(122, 73, 226, 0.28);
  box-shadow: 0 10px 20px rgba(122, 73, 226, 0.08);
}

.calendar-day--selected {
  background: linear-gradient(180deg, rgba(122, 73, 226, 0.12), rgba(255, 255, 255, 0.86));
  border-color: rgba(122, 73, 226, 0.3);
}

.calendar-day--rest .cell-lunar {
  color: #3d7268;
}

.calendar-day--festival .cell-lunar {
  color: #8c6725;
}

.calendar-day--term .cell-lunar {
  color: var(--hl-brand-primary);
}

.calendar-day--work .cell-lunar {
  color: #b66a32;
}

.cell-solar {
  font-size: 16px;
  font-weight: 700;
  color: var(--hl-text-primary);
}

.cell-lunar {
  color: var(--hl-text-secondary);
  font-size: 11px;
  line-height: 1.45;
}

.section {
  padding-top: 34px;
}

.section-header {
  max-width: 760px;
}

.section-kicker {
  margin-bottom: 12px;
}

.section-header h2 {
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.28;
}

.section-header p {
  margin: 12px 0 0;
  font-size: 15px;
}

.card-grid {
  gap: 22px;
  margin-top: 26px;
}

.card {
  flex: 1 1 260px;
  min-width: 240px;
  padding: 22px 22px 20px;
  border-radius: var(--hl-radius-md);
}

.card-tag {
  color: var(--hl-brand-gold);
  background: rgba(198, 144, 42, 0.12);
}

.card h3 {
  font-size: 28px;
  line-height: 1.25;
}

.card p {
  margin: 10px 0 0;
  font-size: 14px;
}

.card-action {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed rgba(122, 73, 226, 0.12);
  color: #5d5489;
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
  font-size: 14px;
}

.site-footer {
  margin-top: 36px;
  padding: 28px 0 10px;
  border-top: 1px solid var(--hl-border-soft);
  text-align: center;
}

.footer-title {
  font-size: 18px;
  line-height: 1.8;
}

.footer-desc {
  max-width: 720px;
  margin: 10px auto 0;
  font-size: 12px;
}

.footer-meta {
  width: 100%;
  max-width: none;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  text-align: center;
  font-size: 11px;
  line-height: 1.7;
}

.footer-meta > div {
  width: 100%;
  text-align: center;
}

.records {
  width: 100%;
  justify-content: center;
  gap: 10px 16px;
}

.records a,
.footer-meta a {
  font-size: 11px;
}

.records a:hover,
.footer-meta a:hover {
  color: var(--hl-brand-primary);
}

@media (max-width: 980px) {
  .page-shell {
    width: min(720px, calc(100% - 32px));
    padding: 18px 0 32px;
  }

  .view-nav {
    margin-bottom: 16px;
  }

  .topbar {
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding-bottom: 20px;
    text-align: center;
  }

  .brand {
    justify-content: center;
    width: 100%;
  }

  .brand-copy,
  .hero-copy {
    text-align: center;
  }

  .top-note {
    max-width: 560px;
    text-align: center;
  }

  .hero-calendar-shell {
    padding: 20px 18px 18px;
    border-radius: 26px;
  }

  .hero-calendar-top {
    flex-direction: column;
  }

  .terms-hero,
  .terms-highlight-head {
    flex-direction: column;
  }

  .hero-calendar-tools {
    justify-content: flex-start;
  }

  .terms-meta-chips {
    justify-content: flex-start;
  }

  .calendar-grid--hero .calendar-day {
    min-height: 76px;
  }

  .terms-detail-grid {
    grid-template-columns: 1fr;
  }

  .terms-link-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .terms-phenology-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .terms-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-grid {
    gap: 16px;
    grid-template-columns: 1fr;
  }

  .content-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .almanac-card {
    padding: 24px 20px 22px;
    border-radius: 28px;
  }

  .panel-head h2 {
    font-size: 34px;
  }

  .panel {
    padding: 20px 18px 18px;
  }

  .hour-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .sidebar {
    gap: 16px;
  }
}

@media (max-width: 640px) {
  .page-shell {
    width: calc(100% - 24px);
    padding: 14px 0 28px;
  }

  .view-nav {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .view-nav-btn {
    width: 100%;
    min-height: 38px;
    padding: 0 12px;
    font-size: 13px;
  }

  .view-nav-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 38px;
    padding: 0 12px;
    font-size: 13px;
  }

  .hero h1 {
    font-size: 34px;
    letter-spacing: 0.04em;
  }

  .hero-desc,
  .section-header p {
    font-size: 14px;
  }

  .hero-calendar-shell {
    margin-top: 18px;
    padding: 18px 14px 14px;
    border-radius: 24px;
  }

  .terms-highlight,
  .terms-grid-section,
  .terms-link-section {
    margin-top: 18px;
    padding: 18px 14px 16px;
    border-radius: 24px;
  }

  .hero-calendar-tools {
    width: 100%;
    gap: 8px;
  }

  .calendar-select-wrap,
  .calendar-select-wrap--month {
    flex: 1 1 auto;
  }

  .calendar-select {
    width: 100%;
    min-width: 0;
  }

  .hero-calendar-tools .month-btn--today {
    flex: 1 1 100%;
    min-width: 0;
  }

  .hero-calendar-tools .month-btn--icon {
    flex: 0 0 36px;
  }

  .weekday-row--hero {
    margin-top: 16px;
  }

  .calendar-grid--hero {
    margin-top: 10px;
  }

  .calendar-grid--hero .calendar-day {
    min-height: 62px;
    padding: 6px 5px;
  }

  .hero-day-strip {
    margin-top: 14px;
    padding: 12px;
    border-radius: 20px;
  }

  .hero-day-date {
    font-size: 28px;
  }

  .hero-day-meta,
  .hero-day-text {
    font-size: 13px;
  }

  .hero-day-row {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 10px;
  }

  .hero-day-tag {
    justify-self: start;
  }

  .hero-day-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
  }

  .terms-highlight h2 {
    font-size: 32px;
  }

  .terms-detail-grid {
    gap: 14px;
    margin-top: 16px;
  }

  .terms-detail-card {
    padding: 14px;
    border-radius: 18px;
  }

  .terms-phenology-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .terms-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-top: 18px;
  }

  .terms-card {
    padding: 14px;
  }

  .terms-card-name {
    font-size: 20px;
  }

  .terms-link-card {
    margin-top: 16px;
    padding: 16px 14px;
    border-radius: 20px;
  }

  .terms-link-copy h3 {
    font-size: 24px;
  }

  .terms-link-btn {
    width: 100%;
    min-width: 0;
  }

  .summary-grid {
    margin-top: 18px;
    gap: 14px;
  }

  .summary-card,
  .card {
    min-width: 0;
    width: 100%;
  }

  .summary-card {
    padding: 18px 18px 16px;
    border-radius: 24px;
  }

  .summary-main {
    font-size: 30px;
  }

  .summary-main--medium {
    font-size: 28px;
  }

  .summary-goodbad {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .summary-goodbad-card {
    padding: 10px;
  }

  .summary-tag {
    min-height: 26px;
    padding: 0 8px;
    font-size: 11px;
  }

  .summary-chips {
    gap: 8px;
    margin-top: 14px;
  }

  .content-grid {
    gap: 16px;
    padding-top: 18px;
  }

  .almanac-card {
    padding: 20px 16px 18px;
    border-radius: 24px;
  }

  .panel {
    padding: 18px 16px 16px;
  }

  .panel-head h2,
  .info-panel h3,
  .pengzu-panel h3,
  .tibetan-panel h3,
  .section-header h2 {
    font-size: 30px;
  }

  .pengzu-panel h3,
  .tibetan-panel h3 {
    font-size: 22px;
  }

  .pillar-grid {
    gap: 8px;
    margin-top: 18px;
  }

  .pillar-item {
    flex-basis: calc(50% - 4px);
    min-width: 0;
    padding: 11px 10px 9px;
  }

  .pillar-item strong {
    font-size: 21px;
  }

  .detail-table-card,
  .hour-panel {
    margin-top: 18px;
  }

  .month-btn {
    min-height: 42px;
  }

  .sidebar {
    gap: 14px;
  }

  .detail-row {
    grid-template-columns: 92px minmax(0, 1fr);
    align-items: stretch;
  }

  .detail-label {
    border-right: 1px solid rgba(122, 73, 226, 0.08);
    border-bottom: 0;
  }

  .detail-label,
  .detail-value {
    padding: 11px 12px;
    font-size: 13px;
  }

  .detail-value {
    line-height: 1.75;
  }

  .pengzu-panel p,
  .tibetan-panel p {
    font-size: 11px;
    line-height: 1.72;
  }

  .hero-calendar-shell,
  .terms-highlight,
  .terms-grid-section,
  .terms-link-section,
  .summary-card,
  .almanac-card,
  .panel,
  .card {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    background:
      radial-gradient(circle at top right, rgba(157, 123, 240, 0.06), transparent 26%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 244, 255, 0.95));
  }

  .hour-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .hour-item {
    padding: 10px 10px 9px;
    border-radius: 16px;
  }

  .hour-head {
    gap: 8px;
  }

  .hour-head strong {
    font-size: 17px;
  }

  .hour-meta,
  .hour-reco {
    font-size: 11px;
  }

  .calendar-head strong {
    font-size: 20px;
  }

  .calendar-head--summary {
    gap: 8px;
  }

  .calendar-head--summary .calendar-note {
    flex: 1 1 100%;
    order: 3;
    text-align: left;
  }

  .weekday-row {
    gap: 6px;
    margin-top: 16px;
  }

  .calendar-grid {
    gap: 6px;
  }

  .calendar-day {
    min-height: 66px;
    padding: 8px 6px;
    border-radius: 16px;
  }

  .calendar-today-badge {
    top: 6px;
    right: 6px;
    width: 16px;
    height: 16px;
    font-size: 10px;
  }

  .cell-solar {
    font-size: 14px;
  }

  .cell-lunar {
    font-size: 10px;
    line-height: 1.35;
  }

  .section {
    padding-top: 28px;
  }

  .card-grid {
    gap: 14px;
    margin-top: 18px;
  }

  .card {
    padding: 18px 18px 16px;
  }

  .card h3 {
    font-size: 24px;
  }

  .site-footer {
    margin-top: 28px;
    padding-top: 24px;
  }
}
</style>
